<script setup lang="ts">
/**
 * A heading's text and rank.
 *
 * The level is chosen from segments rather than a dropdown: there are four of
 * them, they are ordered, and seeing all four at once shows the document's
 * structure in a way a closed menu does not.
 */
import { useI18n } from 'vue-i18n'

import { computed } from 'vue'

import { Switch } from '@/components/ui'
import { ACCENT_PRESET_IDS, ACCENT_PRESETS } from '@/domain/layout'
import type { HeadingSection } from '@/domain/section'
import InspectorField from '@/sections/InspectorField.vue'
import CellColourPicker from '@/sections/table/CellColourPicker.vue'
import TextStyleField from '@/sections/TextStyleField.vue'
import { useReportStore } from '@/stores/report'

const { t } = useI18n()
const report = useReportStore()

const props = defineProps<{ section: HeadingSection }>()
const emit = defineEmits<{ change: [section: HeadingSection] }>()

const LEVELS = [1, 2, 3, 4] as const

function set(changes: Partial<HeadingSection>): void {
  emit('change', { ...props.section, ...changes })
}

/** A setting of the contents page changed: stored only while it says something. */
function setContents(key: 'contentsLabel' | 'contentsDetail', value: string): void {
  const { [key]: _own, ...rest } = props.section
  emit('change', value.trim() === '' ? rest : { ...rest, [key]: value })
}

function setHidden(hidden: boolean): void {
  const { hideInContents: _own, ...rest } = props.section
  emit('change', hidden ? { ...rest, hideInContents: true } : rest)
}

function setColour(colour: string | null): void {
  const { colour: _own, ...rest } = props.section
  emit('change', colour ? { ...rest, colour } : rest)
}

/** The charter's colours first, then the accent presets, as swatches. */
const colours = computed(() => [
  ...report.layout.palette.map((colour, at) => ({
    id: `house-${at + 1}`,
    colour,
    name: t('inspector.heading.houseColour', { number: at + 1 }),
  })),
  ...ACCENT_PRESET_IDS.map((id) => ({
    id,
    colour: ACCENT_PRESETS[id],
    name: t(`document.accents.${id}`),
  })),
])
</script>

<template>
  <div class="fields">
    <InspectorField :label="t('inspector.heading.text')">
      <input
        class="b-input"
        :value="section.title"
        @input="set({ title: ($event.target as HTMLInputElement).value })"
      />
    </InspectorField>

    <InspectorField :label="t('inspector.heading.level')" :hint="t('inspector.heading.levelHint')">
      <div class="levels" role="group" :aria-label="t('inspector.heading.levelGroup')">
        <button
          v-for="level in LEVELS"
          :key="level"
          type="button"
          class="levels__option"
          :class="{ 'levels__option--on': section.level === level }"
          :aria-pressed="section.level === level"
          @click="set({ level })"
        >
          H{{ level }}
        </button>
      </div>
    </InspectorField>

    <h3 class="group">{{ t('inspector.heading.inContents') }}</h3>

    <Switch :model-value="!section.hideInContents" @update:model-value="setHidden(!$event)">
      {{ t('inspector.heading.listed') }}
    </Switch>

    <template v-if="!section.hideInContents">
      <InspectorField
        :label="t('inspector.heading.contentsLabel')"
        :hint="t('inspector.heading.contentsLabelHint')"
      >
        <input
          class="b-input"
          :value="section.contentsLabel ?? ''"
          :placeholder="section.title"
          @input="setContents('contentsLabel', ($event.target as HTMLInputElement).value)"
        />
      </InspectorField>

      <InspectorField
        :label="t('inspector.heading.contentsDetail')"
        :hint="t('inspector.heading.contentsDetailHint')"
      >
        <input
          class="b-input"
          :value="section.contentsDetail ?? ''"
          @input="setContents('contentsDetail', ($event.target as HTMLInputElement).value)"
        />
      </InspectorField>

      <CellColourPicker
        v-if="section.level === 1"
        :label="t('inspector.heading.chapterColour')"
        :options="colours"
        :chosen="section.colour"
        :none-label="t('inspector.heading.chapterColourAuto')"
        :custom-label="t('inspector.table.look.customColour')"
        stacked
        @choose="setColour"
      />
    </template>

    <TextStyleField
      :value="section.textStyleId"
      :hint="t('inspector.heading.textStyleHint')"
      @change="set({ textStyleId: $event })"
    />
  </div>
</template>

<style scoped>
.fields {
  display: flex;
  flex-direction: column;
  gap: var(--sp-6);
}

.group {
  margin: var(--sp-3) 0 0;
  padding-top: var(--sp-6);
  border-top: 1px solid var(--hairline);
  font-size: var(--text-sm);
  font-weight: var(--weight-semibold);
  color: var(--ink);
}

.levels {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1px;
  padding: 2px;
  border: 1px solid var(--hairline-strong);
  border-radius: var(--radius);
  background: var(--panel-raised);
}

.levels__option {
  height: 26px;
  border: 0;
  border-radius: var(--radius-xs);
  background: none;
  color: var(--ink-soft);
  font: inherit;
  font-size: var(--text-sm);
  cursor: pointer;
  transition:
    background var(--quick),
    color var(--quick);
}

.levels__option:hover {
  color: var(--ink);
}

.levels__option--on {
  background: var(--accent-quiet);
  color: var(--accent);
  font-weight: 600;
}
</style>
